// src/modules/demo/data/demoFixtures.ts

import dayjs from 'dayjs';
import { LIST_COLORS } from '../../../constants/constants';
import type {
  DemoClassroom,
  DemoStudent,
  DemoAttendanceRecord,
  DemoHomeworkRecord,
  DemoObservation,
} from '../types';

const DEMO_STUDENTS: Array<{ name: string }> = [
  { name: 'Max Mueller' },
  { name: 'Emma Schmidt' },
  { name: 'Leon Weber' },
  { name: 'Mia Fischer' },
  { name: 'Paul Becker' },
  { name: 'Sophie Meyer' },
  { name: 'Finn Wagner' },
  { name: 'Hannah Schulz' },
];

/** Return the last N business days (Mon-Fri) counting backwards from baseDate (inclusive). */
function getLastBusinessDays(baseDate: string, count: number): string[] {
  const days: string[] = [];
  let current = dayjs(baseDate);

  while (days.length < count) {
    const dow = current.day();
    if (dow !== 0 && dow !== 6) {
      days.push(current.format('YYYY-MM-DD'));
    }
    current = current.subtract(1, 'day');
  }

  return days;
}

// Hardcoded rating matrix: [studentIndex][dayIndex] = rating (0-5)
const RATINGS: number[][] = [
  [4, 5, 4, 5, 4, 4, 5, 4, 5, 4], // Max
  [3, 4, 3, 4, 3, 4, 3, 4, 3, 4], // Emma
  [3, 3, 2, 3, 3, 3, 3, 2, 3, 3], // Leon
  [4, 4, 3, 4, 5, 4, 4, 3, 4, 4], // Mia
  [2, 3, 2, 3, 2, 2, 3, 3, 2, 3], // Paul
  [5, 4, 5, 4, 5, 4, 5, 4, 5, 4], // Sophie
  [3, 2, 3, 2, 3, 2, 2, 3, 2, 3], // Finn
  [4, 3, 4, 3, 4, 3, 3, 4, 3, 4], // Hannah
];

// Hardcoded absence pattern: [dayIndex] = array of absent student indices
const ABSENCES: Record<number, number[]> = {
  1: [6],    // day 1: Finn absent
  3: [7, 4], // day 3: Hannah, Paul absent
  5: [3],    // day 5: Mia absent
  7: [2],    // day 7: Leon absent
  9: [1],    // day 9: Emma absent
};

// Homework on days 2, 5, 8 (0-indexed from most recent)
const HOMEWORK_DAYS = [2, 5, 8];
const HOMEWORK_NAMES = ['Aufgabe 3.4', 'Uebung Kapitel 5', 'Wiederholung'];

// Hardcoded homework statuses per student per homework day
const HOMEWORK_STATUSES: Array<Array<'done' | 'undone' | 'none'>> = [
  ['done', 'done', 'undone', 'done', 'undone', 'done', 'none', 'done'],
  ['done', 'undone', 'done', 'done', 'none', 'done', 'undone', 'done'],
  ['done', 'done', 'done', 'undone', 'done', 'done', 'undone', 'none'],
];

type DemoFixtures = {
  classroom: DemoClassroom;
  students: DemoStudent[];
  attendanceRecords: DemoAttendanceRecord[];
  homeworkRecords: DemoHomeworkRecord[];
  observations: DemoObservation[];
};

export function createDemoFixtures(baseDate: string): DemoFixtures {
  const days = getLastBusinessDays(baseDate, 10);

  const classroom: DemoClassroom = {
    id: 'demo-classroom-1',
    name: 'Klasse 7B',
    subject: 'Mathematik',
    shortName: '7B',
    initYear: 2025,
    endYear: 2026,
  };

  const students: DemoStudent[] = DEMO_STUDENTS.map((s, i) => ({
    id: `demo-student-${i + 1}`,
    name: s.name,
    color: LIST_COLORS[i],
  }));

  const attendanceRecords: DemoAttendanceRecord[] = [];
  let recordId = 1;

  for (let dayIdx = 0; dayIdx < days.length; dayIdx++) {
    const date = days[dayIdx];
    const absentStudents = ABSENCES[dayIdx] || [];

    for (let studentIdx = 0; studentIdx < students.length; studentIdx++) {
      const isAbsent = absentStudents.includes(studentIdx);
      attendanceRecords.push({
        id: `demo-att-${recordId++}`,
        studentId: students[studentIdx].id,
        date,
        attendance: isAbsent ? 'A' : 'P',
        rating: isAbsent ? 0 : RATINGS[studentIdx][dayIdx],
      });
    }
  }

  const homeworkRecords: DemoHomeworkRecord[] = HOMEWORK_DAYS.map((dayIdx, hwIdx) => ({
    id: `demo-hw-${hwIdx + 1}`,
    date: days[dayIdx],
    name: HOMEWORK_NAMES[hwIdx],
    statuses: students.map((s, studentIdx) => {
      const absentStudents = ABSENCES[dayIdx] || [];
      const isAbsent = absentStudents.includes(studentIdx);
      return {
        studentId: s.id,
        status: isAbsent ? ('none' as const) : HOMEWORK_STATUSES[hwIdx][studentIdx],
      };
    }),
  }));

  const observations: DemoObservation[] = [
    {
      id: 'demo-obs-1',
      studentId: 'demo-student-1',
      date: days[3],
      text: 'Heute sehr engagiert bei der Gruppenarbeit',
      createdAt: dayjs(days[3]).hour(10).minute(30).toISOString(),
    },
    {
      id: 'demo-obs-2',
      studentId: 'demo-student-4',
      date: days[5],
      text: 'Hat die Hausaufgaben vergessen',
      createdAt: dayjs(days[5]).hour(9).minute(15).toISOString(),
    },
    {
      id: 'demo-obs-3',
      studentId: 'demo-student-3',
      date: days[7],
      text: 'Praesentation war super, sehr gut vorbereitet',
      createdAt: dayjs(days[7]).hour(11).minute(0).toISOString(),
    },
    {
      id: 'demo-obs-4',
      studentId: 'demo-student-2',
      date: days[9],
      text: 'Braucht mehr Uebung bei Bruchrechnung',
      createdAt: dayjs(days[9]).hour(14).minute(45).toISOString(),
    },
  ];

  return { classroom, students, attendanceRecords, homeworkRecords, observations };
}
